import { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ChevronRight, Pencil, TriangleAlert } from "lucide-react";

import Loading from "@/components/Loading";
import { MarkdownContent } from "@/components/MarkdownContent";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import { getColumnSortAria, SortableColumnHeader } from "@/components/ui/sortable-column-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/table-pagination";
import { cn } from "@/lib/utils";
import { formatDateTimeCompact } from "@/utils/relative-time";

import { getFullName, getTargetLabel } from "../../adapter";
import {
  type SetTermsAndConditionsModalOptions,
  type TermsAndConditionsText,
  type UserSummary,
} from "../../types";

interface ListProps {
  errorMessage?: string;
  isFetching?: boolean;
  onSortingChange: OnChangeFn<SortingState>;
  setModalOptions: SetTermsAndConditionsModalOptions;
  sorting: SortingState;
  termsAndConditionsTexts?: TermsAndConditionsText[];
}

interface TermsAndConditionsColumnMeta {
  cellClassName?: string;
  headerClassName?: string;
}

type TermsAndConditionsColumnDef = ColumnDef<TermsAndConditionsText, unknown> & {
  meta?: TermsAndConditionsColumnMeta;
};

const PAGE_SIZE = 20;
const EMPTY_VALUE = "-";
const COMPACT_HEADER_CLASS = "px-3 py-2 !whitespace-normal leading-tight";
const ACTION_BUTTON_CLASS = "w-[4.75rem]";

export const DEFAULT_SORTING: SortingState = [{ id: "lastUpdated", desc: true }];

const getColumnMeta = (columnDef: ColumnDef<TermsAndConditionsText, unknown>) =>
  columnDef.meta as TermsAndConditionsColumnMeta | undefined;

const getTimestamp = (value?: string | null) => {
  if (!value) {
    return undefined;
  }

  const timestamp = new Date(value).getTime();

  return Number.isNaN(timestamp) ? undefined : timestamp;
};

const PlaceholderValue = () => <span className="text-neutral-alpha-300">{EMPTY_VALUE}</span>;

const AuditCell = ({
  dateTime,
  t,
  user,
}: {
  dateTime?: string | null;
  t: TFunction;
  user?: UserSummary | null;
}) => {
  const formattedDateTime = formatDateTimeCompact(dateTime);

  if (!formattedDateTime) {
    return <PlaceholderValue />;
  }

  return (
    <div className="min-w-0 leading-tight">
      <div className="text-foreground">{formattedDateTime}</div>
      <div className="mt-1 truncate text-muted-foreground text-xs">
        {t("configuration.byFullName", { fullName: getFullName(user) })}
      </div>
    </div>
  );
};

const createColumns = ({
  onOpenModal,
  t,
}: {
  onOpenModal: (record: TermsAndConditionsText, type: "edit" | "view") => void;
  t: TFunction;
}): TermsAndConditionsColumnDef[] => [
  {
    id: "actions",
    header: () => (
      <span className="sr-only">
        {t("common.view")} / {t("common.edit")}
      </span>
    ),
    cell: ({ row }) => (
      <div className="flex items-center justify-start gap-2">
        <WFPButton
          type="button"
          variant="outline"
          size="sm"
          className={ACTION_BUTTON_CLASS}
          onClick={() => onOpenModal(row.original, "view")}
        >
          {t("common.view")}
          <ChevronRight className="size-4" aria-hidden="true" />
        </WFPButton>
        <WFPButton
          type="button"
          variant="secondary"
          size="sm"
          className={ACTION_BUTTON_CLASS}
          onClick={() => onOpenModal(row.original, "edit")}
        >
          <Pencil className="size-4" aria-hidden="true" />
          {t("common.edit")}
        </WFPButton>
      </div>
    ),
    enableSorting: false,
    meta: {
      cellClassName: "w-0 px-3 align-top",
      headerClassName: "w-0",
    },
  },
  {
    id: "target",
    accessorFn: record => getTargetLabel(record.target, t),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("termsAndConditionsText.list.target")} />
    ),
    cell: ({ row }) =>
      row.original.target ? (
        <Badge variant="secondary" className="whitespace-normal text-left">
          {getTargetLabel(row.original.target, t)}
        </Badge>
      ) : (
        <PlaceholderValue />
      ),
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-40 max-w-56 whitespace-normal px-3 align-top",
      headerClassName: cn("min-w-40 max-w-56", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "shortText",
    accessorFn: record => record.short_text?.trim() || "",
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("termsAndConditionsText.list.shortText")} />
    ),
    cell: ({ row }) =>
      row.original.short_text?.trim() ? (
        <MarkdownContent markdown={row.original.short_text} />
      ) : (
        <PlaceholderValue />
      ),
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-64 whitespace-normal px-3 align-top",
      headerClassName: cn("min-w-64", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "version",
    accessorFn: record => (record.version === null ? undefined : record.version),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("termsAndConditionsText.list.version")} />
    ),
    cell: ({ row }) =>
      row.original.version === undefined || row.original.version === null ? (
        <PlaceholderValue />
      ) : (
        String(row.original.version)
      ),
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-32 max-w-44 whitespace-normal break-all px-3 align-top",
      headerClassName: cn("min-w-32 max-w-44", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "lastUpdated",
    accessorFn: record => getTimestamp(record.last_update_datetime),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.list.column.lastUpdated")} />
    ),
    cell: ({ row }) => (
      <AuditCell
        dateTime={row.original.last_update_datetime}
        t={t}
        user={row.original.last_update_user}
      />
    ),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-40 px-3 align-top",
      headerClassName: cn("min-w-40", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "created",
    accessorFn: record => getTimestamp(record.creation_datetime),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.list.column.created")} />
    ),
    cell: ({ row }) => (
      <AuditCell
        dateTime={row.original.creation_datetime}
        t={t}
        user={row.original.creation_user}
      />
    ),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-40 px-3 align-top",
      headerClassName: cn("min-w-40", COMPACT_HEADER_CLASS),
    },
  },
];

export const List = ({
  errorMessage = "",
  isFetching = false,
  onSortingChange,
  setModalOptions,
  sorting,
  termsAndConditionsTexts = [],
}: ListProps) => {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const columns = useMemo(
    () =>
      createColumns({
        onOpenModal: (record, type) =>
          setModalOptions({ id: record.id ?? undefined, isOpen: true, type }),
        t,
      }),
    [setModalOptions, t]
  );

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageIndex === 0 ? currentPagination : { ...currentPagination, pageIndex: 0 }
    );
  }, [sorting]);

  useEffect(() => {
    setPagination(currentPagination => {
      const pageCount = Math.max(
        1,
        Math.ceil(termsAndConditionsTexts.length / currentPagination.pageSize)
      );
      const nextPageIndex = Math.min(currentPagination.pageIndex, pageCount - 1);

      return nextPageIndex === currentPagination.pageIndex
        ? currentPagination
        : { ...currentPagination, pageIndex: nextPageIndex };
    });
  }, [termsAndConditionsTexts.length]);

  const table = useReactTable({
    columns,
    data: termsAndConditionsTexts,
    autoResetPageIndex: false,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (record, index) =>
      record.id === undefined || record.id === null ? String(index) : String(record.id),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange,
    state: { pagination, sorting },
  });

  if (errorMessage) {
    return (
      <Alert variant="destructive">
        <TriangleAlert aria-hidden="true" />
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableHead
                  key={header.id}
                  className={getColumnMeta(header.column.columnDef)?.headerClassName}
                  aria-sort={getColumnSortAria(header.column)}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isFetching ? (
            <TableRow>
              <TableCell colSpan={columnCount}>
                <Loading isVisible small />
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
                {t("termsAndConditionsText.list.empty")}
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching
            ? rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      key={cell.id}
                      className={getColumnMeta(cell.column.columnDef)?.cellClassName}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
        </TableBody>
      </Table>
      {rows.length > 0 ? <DataTablePagination table={table} /> : null}
    </div>
  );
};
